import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';
import type { RenderedChart } from './charts';
import { displayCell, missingLabel } from './format';
import { clientCombinable, serverTotals, totalCell, type TotalOptions } from './result-totals';
import { pivotRowCount } from './pivot-layout';
import { isTimeColumn, timeValue } from './chart-time';

const typedKey = (value: unknown): string => JSON.stringify([typeof value, value ?? null]);
const escape = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export interface PivotOptions {
    /** The report's sort; a sort on a grouping field orders that axis. */
    sort?: Array<{ by: string; dir: 'asc' | 'desc' }> | null;
    /** False for dataset measures whose group values must not be added again (see result-totals.ts). */
    reaggregable?: TotalOptions['reaggregable'];
}

/** Natural order for one grouping field: time chronologically, numbers numerically, text naturally; missing last. */
export function axisComparator(field: QueryResultColumn, dir: 'asc' | 'desc' = 'asc'): (a: unknown, b: unknown) => number {
    const collator = typeof Intl !== 'undefined' ? new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }) : null;
    const time = isTimeColumn(field);
    const sign = dir === 'desc' ? -1 : 1;
    return (a, b) => {
        const am = a == null || a === '', bm = b == null || b === '';
        // Missing groups always follow real ones, (Empty) before (No value), whatever the direction.
        if (am || bm) return am && bm ? (a === b ? 0 : a == null ? 1 : -1) : am ? 1 : -1;
        if (time) { const x = timeValue(a), y = timeValue(b); if (x != null && y != null && x !== y) return sign * (x - y); }
        if (typeof a === 'number' && typeof b === 'number') return sign * (a - b);
        if (typeof a === 'boolean' && typeof b === 'boolean') return sign * (Number(b) - Number(a));
        const x = String(a), y = String(b);
        if (field.dataType === 'number' && Number.isFinite(Number(x)) && Number.isFinite(Number(y))) return sign * (Number(x) - Number(y));
        return sign * (collator ? collator.compare(x, y) : x < y ? -1 : x > y ? 1 : 0);
    };
}

export function renderPivotTable(host: HTMLElement, result: QueryResult, display: QueryDisplay | null,
    fallback: () => RenderedChart, options: PivotOptions = {}): RenderedChart {
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measures = result.columns.filter(c => c.kind === 'measure');
    if (dims.length < 2 || measures.length < 1) return fallback();
    const split = pivotRowCount(dims.length, display);
    const rowFields = dims.slice(0, split), columnFields = dims.slice(split);
    type AxisGroup = { key: string; values: unknown[] };
    const rows = new Map<string, AxisGroup>(), columns = new Map<string, AxisGroup>();
    const cells = new Map<string, Map<string, Record<string, unknown>>>();
    // Typed tuples distinguish null, empty text, delimiters and identically formatted values.
    const group = (record: Record<string, unknown>, fields: QueryResultColumn[]): AxisGroup => {
        const values = fields.map(f => record[f.alias]);
        return { key: JSON.stringify(values.map(typedKey)), values };
    };
    for (const record of result.rows) {
        const row = group(record, rowFields), column = group(record, columnFields);
        rows.set(row.key, row); columns.set(column.key, column);
        const values = cells.get(row.key) ?? new Map<string, Record<string, unknown>>();
        // Never overwrite or sum pre-aggregated averages or distinct counts.
        if (values.has(column.key)) return fallback();
        values.set(column.key, record); cells.set(row.key, values);
    }
    // Axes follow their own values (or the report's sort on that field), never the order rows arrived in.
    const comparators = dims.map(field => {
        const sort = options.sort?.find(s => s.by === field.alias);
        return axisComparator(field, sort?.dir ?? 'asc');
    });
    const tupleCompare = (fields: QueryResultColumn[]) => (a: AxisGroup, b: AxisGroup): number => {
        for (let i = 0; i < fields.length; i++) {
            const c = comparators[dims.indexOf(fields[i])](a.values[i], b.values[i]);
            if (c) return c;
        }
        return 0;
    };
    const rowGroups = [...rows.values()].sort(tupleCompare(rowFields));
    const columnGroups = [...columns.values()].sort(tupleCompare(columnFields));

    // Totals: the engine's when it sent them for that grouping; otherwise only what can be added up honestly from
    // these cells (never across currencies, never dataset measures that must not be added again).
    const server = serverTotals(result);
    const clientTotals = !result.truncated;
    const totalOptions: TotalOptions = { display, clientTotals, reaggregable: options.reaggregable };
    const combinable = (m: QueryResultColumn) => clientCombinable(m, totalOptions);
    const serverCovers = (m: QueryResultColumn) => !!server?.grand.some(entry => m.alias in entry);
    const showTotals = !!server || (clientTotals && measures.some(combinable));
    const skipped = measures.filter(m => !combinable(m) && !serverCovers(m));
    const totalsNote = !showTotals
        ? result.truncated ? 'Totals are not shown because this result is partial.' : 'Totals are not shown: averages and distinct counts cannot be added up across groups.'
        : skipped.length ? `Totals are not shown for ${skipped.map(m => m.label ?? m.alias).join(', ')}: averages and distinct counts cannot be added up across groups.` : '';
    const emptyAsZero = (display as (QueryDisplay & { pivotEmptyCells?: string }) | null)?.pivotEmptyCells === 'zero';

    type RowNode = { id: number; level: number; value: unknown; parent?: RowNode; children: Map<string, RowNode>; row?: AxisGroup; leaves: AxisGroup[] };
    const roots = new Map<string, RowNode>(), nodes: RowNode[] = [], collapsed = new Set<number>();
    for (const row of rowGroups) {
        let siblings = roots, parent: RowNode | undefined;
        row.values.forEach((value, level) => {
            const key = typedKey(value);
            let node = siblings.get(key);
            if (!node) {
                node = { id: nodes.length, level, value, parent, children: new Map(), leaves: [] };
                siblings.set(key, node); nodes.push(node);
            }
            node.leaves.push(row);
            if (level === rowFields.length - 1) node.row = row;
            siblings = node.children; parent = node;
        });
    }
    // Page terminal visible groups, keeping their ancestors on every page. Collapsed
    // groups occupy one slot, so collapsing never strands descendants on other pages.
    const visibleGroups = (): RowNode[] => {
        const visible: RowNode[] = [];
        const visit = (node: RowNode): void => {
            if (!node.children.size || collapsed.has(node.id)) visible.push(node);
            else node.children.forEach(visit);
        };
        roots.forEach(visit); return visible;
    };
    const rowPageSize = 50, columnPageSize = Math.max(1, Math.floor(20 / measures.length));
    let rowStart = 0, columnStart = 0, disposed = false;
    const label = (value: unknown, field: QueryResultColumn): string => missingLabel(value) ?? displayCell(value, field, display);
    const valueText = (measure: QueryResultColumn, record: Record<string, unknown> | undefined): { text: string; empty: boolean } => {
        if (!record) {
            // No records in this combination: blank, or zero for counts when the author asked for zeros.
            if (emptyAsZero && (measure.aggregate === 'count' || measure.aggregate === 'countDistinct')) return { text: displayCell(0, measure, display), empty: false };
            return { text: '', empty: true };
        }
        return { text: displayCell(record[measure.alias], measure, display, { row: record }), empty: false };
    };
    const cellHtml = (measure: QueryResultColumn, record: Record<string, unknown> | undefined): string => {
        const { text, empty } = valueText(measure, record);
        return empty ? '<td data-empty="true"><span class="sr-only">No records</span></td>' : `<td>${escape(text)}</td>`;
    };
    const totalHtml = (measure: QueryResultColumn, records: Array<Record<string, unknown>>, serverEntries: Array<Record<string, unknown>> | null = null, header = false): string => {
        const { known, text, empty } = totalCell(measure, serverEntries, records, totalOptions);
        const tag = header ? 'th' : 'td';
        if (empty) return `<${tag} data-empty="true"><span class="sr-only">No records</span></${tag}>`;
        if (!known) return `<${tag} data-total="unavailable"><span class="sr-only">Not totalled</span></${tag}>`;
        return `<${tag} data-total="true">${escape(text)}</${tag}>`;
    };
    /** The engine's total for the group where `fields` take `values`, when it sent one. */
    const serverFind = (fields: QueryResultColumn[], values: unknown[]) => server?.find(fields, values) ?? null;
    const pathValues = (node: RowNode): unknown[] => {
        const values: unknown[] = [];
        for (let current: RowNode | undefined = node; current; current = current.parent) values.unshift(current.value);
        return values;
    };
    // Records behind each total, gathered once so paging a large pivot never rescans every cell.
    const byColumn = new Map<string, Array<Record<string, unknown>>>();
    for (const values of cells.values()) for (const [key, record] of values) { const list = byColumn.get(key) ?? []; list.push(record); byColumn.set(key, list); }
    const rowRecords = (rowsIn: AxisGroup[]): Array<Record<string, unknown>> => rowsIn.flatMap(r => [...(cells.get(r.key)?.values() ?? [])]);
    const cellRecords = (rowsIn: AxisGroup[], column: AxisGroup): Array<Record<string, unknown>> => {
        const out: Array<Record<string, unknown>> = [];
        for (const r of rowsIn) { const rec = cells.get(r.key)?.get(column.key); if (rec) out.push(rec); }
        return out;
    };
    const axisControls = (axis: 'rows' | 'columns', start: number, size: number, total: number): string => {
        const title = axis === 'rows' ? 'Rows' : 'Columns';
        return `<div class="flex items-center gap-2 flex-wrap" role="group" aria-label="Pivot ${axis}">
            <span role="status" aria-live="polite">${title}: Page ${Math.floor(start / size) + 1} of ${Math.max(1, Math.ceil(total / size))} · ${total ? start + 1 : 0}–${Math.min(start + size, total)} of ${total.toLocaleString()} visible groups</span>
            ${total > size ? `<button type="button" class="sp-button sp-button--neutral sp-button--sm" data-pivot-page="${axis}-previous" aria-label="Previous ${axis} page" ${start === 0 ? 'disabled' : ''}>Previous</button>
            <button type="button" class="sp-button sp-button--neutral sp-button--sm" data-pivot-page="${axis}-next" aria-label="Next ${axis} page" ${start + size >= total ? 'disabled' : ''}>Next</button>` : ''}</div>`;
    };
    const measureName = (m: QueryResultColumn) => escape(m.label ?? m.alias);
    const markup = (): string => {
        const visible = visibleGroups();
        const pageRows = visible.slice(rowStart, rowStart + rowPageSize);
        const included = new Map<number, RowNode>();
        for (const row of pageRows) {
            const path: RowNode[] = [];
            for (let node: RowNode | undefined = row; node; node = node.parent) path.unshift(node);
            path.forEach(node => included.set(node.id, node));
        }
        const pageColumns = columnGroups.slice(columnStart, columnStart + columnPageSize);
        const multi = measures.length > 1;
        const headerRows = columnFields.length + (multi ? 1 : 0);
        const header = columnFields.map((field, level) => {
            let html = level === 0 ? `<th scope="col" rowspan="${headerRows}" class="text-start">${escape(rowFields.map(f => f.label ?? f.alias).join(' › '))}</th>` : '';
            for (let i = 0; i < pageColumns.length;) {
                let end = i + 1;
                const prefix = (column: AxisGroup) => JSON.stringify(column.values.slice(0, level + 1).map(typedKey));
                while (end < pageColumns.length && prefix(pageColumns[end]) === prefix(pageColumns[i])) end++;
                const span = (end - i) * measures.length;
                html += `<th scope="${span > 1 ? 'colgroup' : 'col'}" colspan="${span}">${escape(field.label ?? field.alias)}: ${escape(label(pageColumns[i].values[level], field))}</th>`;
                i = end;
            }
            if (level === 0 && showTotals) html += `<th scope="${multi ? 'colgroup' : 'col'}" colspan="${measures.length}" rowspan="${columnFields.length}" data-total-heading="row">Total</th>`;
            return `<tr>${html}</tr>`;
        }).join('') + (multi ? `<tr>${pageColumns.map(() => measures.map(m => `<th scope="col">${measureName(m)}</th>`).join('')).join('')}${showTotals ? measures.map(m => `<th scope="col">${measureName(m)}</th>`).join('') : ''}</tr>` : '');
        const body = [...included.values()].map(node => {
            const field = rowFields[node.level], text = escape(label(node.value, field));
            const branch = node.children.size > 0, expanded = !collapsed.has(node.id);
            const heading = branch ? `<button type="button" class="analytics-pivot-toggle" data-pivot-toggle="${node.id}" aria-expanded="${expanded}" aria-label="${expanded ? 'Collapse' : 'Expand'} ${escape(field.label ?? field.alias)}: ${text}"><span aria-hidden="true">${expanded ? '−' : '+'}</span> ${text}</button>` : text;
            const values = branch
                ? showTotals
                    // Group rows show subtotals when they can be computed; otherwise they are headings only.
                    ? pageColumns.map(column => measures.map(m => totalHtml(m, cellRecords(node.leaves, column),
                        serverFind([...rowFields.slice(0, node.level + 1), ...columnFields], [...pathValues(node), ...column.values]))).join('')).join('')
                    : `<td colspan="${Math.max(1, pageColumns.length * measures.length)}"><span class="sr-only">Group heading; values appear in the lowest-level rows.</span></td>`
                : pageColumns.map(column => measures.map(m => cellHtml(m, cells.get(node.row!.key)?.get(column.key))).join('')).join('');
            const rowTotal = showTotals ? measures.map(m => totalHtml(m, rowRecords(node.leaves),
                serverFind(rowFields.slice(0, node.level + 1), pathValues(node)))).join('') : '';
            return `<tr data-pivot-row="${branch ? 'group' : 'leaf'}"><th scope="row" class="text-start" data-pivot-level="${node.level}">${heading}</th>${values}${rowTotal}</tr>`;
        }).join('');
        const footer = showTotals ? `<tfoot><tr data-pivot-row="total"><th scope="row" class="text-start">Total</th>${pageColumns.map(column => measures.map(m => totalHtml(m, byColumn.get(column.key) ?? [],
            serverFind(columnFields, column.values))).join('')).join('')}${measures.map(m => totalHtml(m, result.rows, server?.grand.length ? server.grand : null)).join('')}</tr></tfoot>` : '';
        const branches = nodes.filter(node => node.children.size);
        return `${branches.length ? `<div role="group" aria-label="Row group controls" class="flex items-center gap-2 mb-2">
            <button type="button" class="sp-button sp-button--neutral sp-button--sm" data-pivot-expand="all" ${collapsed.size === 0 ? 'disabled' : ''}>Expand all</button>
            <button type="button" class="sp-button sp-button--neutral sp-button--sm" data-pivot-expand="none" ${collapsed.size === branches.length ? 'disabled' : ''}>Collapse all</button></div>` : ''}
            <div class="table-responsive" tabindex="0" role="region" aria-label="Pivot data">
            <table class="table table-sm analytics-pivot"><caption class="sr-only">${measures.map(measureName).join(' and ')} by ${escape(dims.map(c => c.label ?? c.alias).join(' and '))}. Blank cells have no records; (No value) means the value is missing.${rowFields.length > 1 ? ` Expand or collapse row groups.${showTotals ? ' Group rows show subtotals.' : ' Values are shown at the lowest level; group headings are not subtotals.'}` : ''}</caption>
            <thead>${header}</thead><tbody>${body}</tbody>${footer}</table></div>
            <div class="flex items-center gap-4 flex-wrap mt-2" aria-label="Pivot pagination">${axisControls('rows', rowStart, rowPageSize, visible.length)}${columnGroups.length > columnPageSize ? axisControls('columns', columnStart, columnPageSize, columnGroups.length) : ''}</div>${totalsNote ? `<p class="analytics-pivot-note small mt-2" role="note" aria-label="About totals">${escape(totalsNote)}</p>` : ''}`;
    };
    host.innerHTML = `<div data-pivot-grid>${markup()}</div>`;
    const content = host.querySelector?.<HTMLElement>('[data-pivot-grid]');
    const bind = (): void => {
        // Each row heading's depth drives its indent step, which the stylesheet takes from the spacing tokens.
        content?.querySelectorAll<HTMLElement>('[data-pivot-level]').forEach(cell => cell.style.setProperty('--pivot-level', cell.dataset.pivotLevel ?? '0'));
        content?.querySelectorAll<HTMLButtonElement>('[data-pivot-expand]').forEach(button => button.addEventListener('click', () => {
            if (disposed || button.disabled) return;
            const expand = button.dataset.pivotExpand === 'all';
            collapsed.clear();
            if (!expand) nodes.forEach(node => { if (node.children.size) collapsed.add(node.id); });
            rowStart = 0;
            content!.innerHTML = markup(); bind();
            content!.querySelector<HTMLButtonElement>(`[data-pivot-expand="${expand ? 'none' : 'all'}"]`)?.focus();
        }));
        content?.querySelectorAll<HTMLButtonElement>('[data-pivot-toggle]').forEach(button => button.addEventListener('click', () => {
            if (disposed) return;
            const id = Number(button.dataset.pivotToggle);
            if (collapsed.has(id)) collapsed.delete(id); else collapsed.add(id);
            // Keep the toggled group in view, including when it began on a prior page.
            const visible = visibleGroups();
            const index = visible.findIndex(node => {
                for (let ancestor: RowNode | undefined = node; ancestor; ancestor = ancestor.parent) if (ancestor.id === id) return true;
                return false;
            });
            rowStart = Math.floor(Math.max(0, index) / rowPageSize) * rowPageSize;
            content!.innerHTML = markup(); bind();
            content!.querySelector<HTMLButtonElement>(`[data-pivot-toggle="${id}"]`)?.focus();
        }));
        content?.querySelectorAll<HTMLButtonElement>('[data-pivot-page]').forEach(button => {
        button.addEventListener('click', () => {
            if (disposed || button.disabled) return;
            const action = button.dataset.pivotPage!;
            const delta = action.endsWith('next') ? 1 : -1;
            if (action.startsWith('rows')) rowStart = Math.max(0, Math.min(Math.floor((visibleGroups().length - 1) / rowPageSize) * rowPageSize, rowStart + delta * rowPageSize));
            else columnStart = Math.max(0, Math.min(Math.floor((columnGroups.length - 1) / columnPageSize) * columnPageSize, columnStart + delta * columnPageSize));
            content!.innerHTML = markup(); bind();
            const next = content!.querySelector<HTMLButtonElement>(`[data-pivot-page="${action}"]`);
            (next?.disabled ? content!.querySelector<HTMLButtonElement>(`[data-pivot-page="${action.split('-')[0]}-${delta > 0 ? 'previous' : 'next'}"]`) : next)?.focus();
        });
        });
    };
    bind();
    return { destroy() { disposed = true; } };
}
